import React, {useState, useRef, useEffect} from "react";
import { useDispatch, useSelector } from "react-redux";

import { DropDownField, TextInputField, CalendarField } from "../../../helpers";
import Button from "../../../helpers/Button";
import { Menu } from 'primereact/menu';

import { utils, write } from "xlsx";
import Papa from "papaparse";
import { raiseToast } from "../../../services/redux/reducers/toast/toast";
import { updateChequeBooksSearchOptions, updateChequeBooksSearchParams } from "../../../services/redux/reducers/common"
import { getAddFilterOptions, normalizeLabel, sortArrayOfObjectsInAlphabeticalOrder } from "../../../utils";
import useTranslationWithLoading from "../../../common/hooks/useTranslationWIthLoading";

const ChequeBookSearchConfig = [
    {
        srNO: 1,
        static: true,
        type: "SELECT",
        name: "dataType",
        label: "Data type",
        options: [
            { label: 'Cheque Books Data', value: 'chequeBooksData' }
        ]
    }, 
    {   srNO: 3,
        static: true,
        type: "SEARCH_TEXT",
        name: "searchText",
        label: "Search Text",
    },
    {
        srNO: 4,
        static: false,
        type: "SELECT",
        name: "statusId",
        label: "Status",
        options: [
            { label: 'No Filters', value: 'all' },
            { label: 'Used', value: 'used' },
            { label: 'Unused', value: 'unused' },
            { label: 'Realised', value: 'realised' },
            { label: 'Unrealised', value: 'unrealised' },
            { label: 'Deactivated', value: 'deactivated' }
        ]
    },
    {
        srNO: 5,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "sellerName",
        label:"Seller Name",
    },
    {
        srNO: 6,
        static: false,
        removable: true,
        isSearchByOption: true,
        type: "TEXT",
        name: "sellerCompanyName",
        label:"Seller Company Name",
    },
    {
        srNO: 7,
        type: "SELECT",
        static: false,
        removable: true,
        name: "chequeSeries",
        label: "Cheque Series",
        options: [
            { label: 'No Filters', value: 'all' }
        ]
    }
]

const Search = (props) => {
    const { searchCheques, onSearchConfigUpdate } = props
    const [searchConfig, setSearchConfig] = useState(ChequeBookSearchConfig)
    const [isLoading, setIsLoading] = useState(false)

    const dispatch = useDispatch()
    
    const chequeBooks = useSelector(state => state.common.chequeBooks)
    const searchParams = useSelector(state => state.common.chequeBooksSearchParams)
    const searchOptions = useSelector(state => state.common.chequeBooksSearchOptions)

    const filterMenu = useRef(null)
    const downloadMenu = useRef(null)
    const searchTextRef = useRef(null)

    useEffect(()=>{
        const filteredSearchOptions = ChequeBookSearchConfig.filter(eachOption => eachOption.static)
        dispatch(updateChequeBooksSearchOptions(filteredSearchOptions))
    }, [ChequeBookSearchConfig])

    const addFilterOption = (srNO) => {
        let relatedConfig = ChequeBookSearchConfig.find(eachConfig => eachConfig.srNO === srNO)

        if (relatedConfig.name === "chequeSeries"){
            let sortedChequeBooksSeries = getChequeBooksSeries()
            relatedConfig = {...relatedConfig, options: [...relatedConfig.options, ...sortedChequeBooksSeries ]}
        }
        dispatch(updateChequeBooksSearchOptions([...searchOptions, relatedConfig]))
    }

    useEffect(() => {
        if (searchTextRef.current) {
            searchTextRef.current.focus()
        }
    }, [searchParams.searchBy])

    useEffect(() => {
        onSearchConfigUpdate(searchConfig);
    }, [searchConfig])

    const getChequeBooksSeries = () => {
        const chequeBooksSeriesTemplate = []
        chequeBooks.filter((eachChequeBook) => {
            if(eachChequeBook.statusId === 1){
                const optionObj = {
                    label: `${eachChequeBook.companyBankAccounts.bankName} | ${eachChequeBook.chequeSeriesFrom} To ${eachChequeBook.chequeSeriesTo}`,
                    value: eachChequeBook.chequeBookId
                }
                chequeBooksSeriesTemplate.push(optionObj)
            }
        })
        const sortedChequeBooksSeries = sortArrayOfObjectsInAlphabeticalOrder(chequeBooksSeriesTemplate, "label")
        return sortedChequeBooksSeries
    }

    const removeFilterOption = (srNO) => {
        const relatedSearchOption = searchOptions.find(eachOption => eachOption.srNO === srNO)
        const updatedSearchParams = {...searchParams}

        if(relatedSearchOption.type === "SEARCH_BY"){
            updatedSearchParams.searchText = ""
            updatedSearchParams.searchBy = "all"
            dispatch(updateChequeBooksSearchParams(updatedSearchParams))
        }
        if(relatedSearchOption.type === "SELECT"){
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateChequeBooksSearchParams(updatedSearchParams))
        }
        if(relatedSearchOption.type === "TEXT"){
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateChequeBooksSearchParams(updatedSearchParams))
        }
        if (relatedSearchOption.type === "MULTISELECT") {
            updatedSearchParams[relatedSearchOption.name] = ""
            dispatch(updateChequeBooksSearchParams(updatedSearchParams))
        }

        const filteredSearchOptions = searchOptions.filter(eachOption => eachOption.srNO !== srNO)
        dispatch(updateChequeBooksSearchOptions([...filteredSearchOptions]))
    }

    const handleSearchOnChange = (event) => {
        let eventValue = event.target.value
        const updatedSearchParams = {...searchParams, [event.target.name]: event.target.value}

        if (eventValue instanceof Date) {
            if(new Date(searchParams[event.target.name])?.getTime() != eventValue.getTime()){
                updatedSearchParams.range = null
            }

            if (event.target.name === "toDate") {
                eventValue.setHours(23, 59, 0, 0)
            }
            if (event.target.name === "fromDate") {
                eventValue.setHours(0, 0, 0, 0)
            }
            eventValue = eventValue.toISOString()

            updatedSearchParams[event.target.name] = eventValue
        }

        dispatch(updateChequeBooksSearchParams(updatedSearchParams))
    }

    const addFilterOptions = getAddFilterOptions(searchConfig, searchOptions, searchParams, addFilterOption)

    const downloadData = async (dataFormat) => {
        const response = {} 
        // await searchCompanies({...searchParams, paginator: false})
        const responseData = response.data.data
        if(dataFormat === "CSV"){
            let csv = Papa.unparse(responseData, { download: true })
            let blob = new Blob([csv])
            let a = window.document.createElement("a")
            a.href = window.URL.createObjectURL(blob, { type: "text/plain" })
            a.download = "users.csv"
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
        }
        if(dataFormat === "XLSX"){
            const ws = utils.json_to_sheet(responseData)
            const wb = utils.book_new()
            utils.book_append_sheet(wb, ws, 'Sheet1')
            const blob = new Blob([write(wb, { bookType: 'xlsx', type: 'array' })])
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'users.xlsx'
            a.click()
            URL.revokeObjectURL(url)
        }
    }

    const downloadFormatOptions = [
        {
            label: "XLSX",
            command: () => downloadData("XLSX")
        },
        {
            label: "CSV",
            command: () => downloadData("CSV")
        }
    ]

    const handleSearchKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSearch()
        }
    } 
    
    const handleSearch = () => {
        let errorFound = false
        
        if (searchParams.searchText === "" && searchParams.searchBy !== "all") {
            dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: "Please enter search text" }))
            errorFound = true
        } 
        else if (searchParams.searchText && searchParams.searchBy !== "all"){
            const relatedSearchOption = searchConfig.find(eachOption => eachOption.name === searchParams.searchBy)
            if(relatedSearchOption.isValid && !relatedSearchOption.isValid(searchParams.searchText.split(";")[0])){
                dispatch(raiseToast({ severity: 'error', summary: normalizeLabel(searchParams.searchBy), detail: `Please enter valid ${normalizeLabel(searchParams.searchBy).toLowerCase()}` }))
                errorFound = true
            }
        }
        
        searchOptions.map(eachOption => { 
            if (["TEXT", "SELECT", "AUTO_COMPLETE"].includes(eachOption.type)) {
                if (!searchParams[eachOption.name] && searchParams[eachOption.name] !== false) {
                    const errorMsgText = normalizeLabel(eachOption.label)
    
                    dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter ${errorMsgText.toLowerCase()}` }))
                    errorFound = true
                } else {
                    if(eachOption.isValid && !eachOption.isValid(searchParams[eachOption.name].split(";")[0])){
                        const errorMsgText = normalizeLabel(eachOption.label)
                        dispatch(raiseToast({ severity: 'error', summary: errorMsgText, detail: `Please enter valid ${errorMsgText.toLowerCase()}` }))
                        errorFound = true
                    }
                }
            } 
        })

        if(!errorFound){
            searchCheques()
        }
    }
    
    return(
        <div className="p-card" style={{border:"1px solid #e9ecef"}} onKeyDown={handleSearchKeyPress}>
            <div className="grid"> 
                {
                    searchOptions.map(eachOption => {
                        if (eachOption.type === "SEARCH_TEXT") {

                            let searchByOptions = [{label: 'No Filters', value: 'all'}]
                            searchConfig.map(eachSearchBy => {
                                if (eachSearchBy.isSearchByOption) {
                                    const optionInUse = searchOptions.find(eachSearchOption => eachSearchOption.srNO === eachSearchBy.srNO)
                                    if(!optionInUse){
                                        searchByOptions.push({ label: eachSearchBy.label, value: eachSearchBy.name })
                                    }
                                }
                            })
    
                            return (
                                <>
                                    <div className="flex flex-column col-12">
                                        <TextInputField
                                            name="searchText"
                                            labelText= "Search Text"
                                            placeholder="No Filter"
                                            refName={searchTextRef}
                                            value={searchParams["searchText"]}
                                            onChange={handleSearchOnChange}
                                        />
                                    </div>
                                    {searchParams.searchText && (
                                        <div className="flex flex-column col-12">
                                            <DropDownField
                                                name="searchBy"
                                                labelText="Search By"
                                                options={searchByOptions}
                                                value={searchParams["searchBy"]}
                                                onChange={handleSearchOnChange}
                                            />
                                        </div>
                                    )}
                                </>
                            )
                        }
                        
                        if(eachOption.type === "SELECT"){
                            const updatedOptions = eachOption.options.map(eachSelectOption => ({label: eachSelectOption.label, value: eachSelectOption.value}))
                            return (
                                <>
                                    { !eachOption.static ? 
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={()=> removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                        :
                                        null 
                                    }
                                    <div className="flex flex-column col-12">
                                        <DropDownField name={eachOption.name} options={updatedOptions} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                    </div>
                                </>
                            )
                        }

                        if (eachOption.type === "TEXT") {
                            return (
                                <>
                                    {!eachOption.static &&
                                        <div className="flex justify-content-end p-0 col-12 remove-opt">
                                            <i className="pi pi-minus-circle remove-opt-icon" onClick={() => removeFilterOption(eachOption.srNO)} ></i>
                                        </div>
                                    }
                                    {
                                        <div className="flex flex-column col-12">
                                            <TextInputField name={eachOption.name} labelText={eachOption.label} value={searchParams[eachOption.name]} onChange={handleSearchOnChange} />
                                        </div>
                                    }
                                </>
                            )
                        }
                    })
                }
                <div className="flex col-12 justify-content-end">
                    <Menu model={addFilterOptions} popup ref={filterMenu} />
                    <Button icon="pi pi-filter" className="h-2rem p-2 action-btn hover:surface-300 hover:text-700" label="Add Filter" onClick={(e) => filterMenu.current.toggle(e)} />
                </div>

                <div className="flex flex-column col-12 mb-0 pb-0">
                    <div className="grid">
                        <div className="flex col-12 mb-0 gap-1 pb-0">
                            <Button label="Search" style={{flexGrow: 1}} className="py-2" onClick={handleSearch} />
                            <Menu model={downloadFormatOptions} popup ref={downloadMenu} />
                            <Button icon="pi pi-download" className="py-2 px-3"  onClick={(e) => downloadMenu.current.toggle(e)} permissions={[250]}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Search;
